import {
  Button,
  Card,
  Col,
  Input,
  Popover,
  Row,
  Skeleton,
  Space,
  Table,
  Tooltip,
} from "antd";
import { useContext, useEffect, useState } from "react";
import { useTheme } from "../../Context/ThemeContext";
import { useClient } from "../../Context/ClientContext";
import { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { LedgerStatus } from "../../Services/LedgerService";
import { notificationContext } from "../../Common/PageRoute";
import OrganisationsService, {
  IEmpData,
} from "../../Services/Organisations/OrganisationsService";
import { OrgDataType } from "./Organisations";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import { IIdNameModel } from "../../Services/ItemsService";
import { ConfirmationModal } from "../../Common/ConfirmationModal";
import AddEmpModel from "./AddEmpModel";

interface EmpDataType extends OrgDataType {
  empName: string;
  empCode?: string;
  fatherName?: string;
  email?: string;
  createLoginCredential: boolean;
  subUnitName: string;
  depttName: string;
  designationName: string;
  seniorEmpId?: string;
}

const Employees = () => {
  const { currentClient } = useClient();
  const [openEmpModel, setOpenEmpModel] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    open: boolean;
    id: string;
  }>({
    open: false,
    id: "",
  });
  const [addUpdateTrigger, setAddUpdateTrigger] = useState<boolean>(false);
  const [searchEmp, setSearchEmp] = useState<string>("");
  const { themeData } = useTheme();
  const { openNotification } = useContext(notificationContext);
  const [empList, setEmpList] = useState<EmpDataType[]>([]);
  const [dataLoading, setDataLoading] = useState<boolean>(false);

  const getEmpList = async () => {
    setDataLoading(true);
    await OrganisationsService.getEmployeesList(
      currentClient?.id!,
      searchEmp,
      "",
      "",
      LedgerStatus.active
    ).then((val: IEmpData[] | null) => {
      setEmpList(
        val?.map<EmpDataType>((emp: IEmpData, index: number) => {
          return {
            sno: index + 1,
            id: emp.id as string,
            empName: emp.empName,
            empCode: emp.empCode,
            email: emp.email,
            fatherName: emp.fatherName,
            seniorEmpId: emp.seniorEmpId,
            createLoginCredential: emp.createLoginCredential,
            orgName: emp.orgName as string,
            subUnitName: emp.subUnitName as string,
            depttName: emp.depttName as string,
            designationName: emp.designationName as string,
            status: emp.status!,
          };
        }) || []
      );
    });
    setDataLoading(false);
  };

  const handleDelete = async (selId: string) => {
    if (selId) {
      await OrganisationsService.deleteEmployee(currentClient?.id!, selId)
        .then((val: IApiResponse<IIdNameModel>) => {
          if (val?.status) {
            openNotification(
              "success",
              val?.result?.name + " employee deleted successfully"
            );
            setAddUpdateTrigger((prev) => !prev);
          } else {
            openNotification("error", val?.errors && val?.errors[0]?.message);
          }
        })
        .catch((err) => {
          openNotification("error", err?.message || "");
        });
      setDeleteConfirmation({
        open: false,
        id: "",
      });
    }
  };

  useEffect(() => {
    if (currentClient?.id) {
      getEmpList();
    }
  }, [currentClient?.id, addUpdateTrigger, searchEmp]);

  // --------- Departments Table Columns  ---------
  const depttCols: ColumnsType<EmpDataType> = [
    {
      title: "S.No.",
      dataIndex: "sno",
      key: "sno",
      width: "5%",
      render: (value: number) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Employee's Name",
      dataIndex: "empName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Father's Name",
      dataIndex: "fatherName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Designation",
      dataIndex: "designationName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Department",
      dataIndex: "depttName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    {
      title: "Sub Unit",
      dataIndex: "subUnitName",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          {value}
        </Skeleton>
      ),
    },
    // {
    //   title: "Organisation Name",
    //   dataIndex: "orgName",
    //   render: (value: string) => (
    //     <Skeleton
    //       active
    //       loading={dataLoading}
    //       paragraph={{ rows: 1 }}
    //       title={false}
    //     >
    //       {value}
    //     </Skeleton>
    //   ),
    // },
    {
      title: "Action",
      dataIndex: "id",
      width: "10%",
      render: (value: string) => (
        <Skeleton
          active
          loading={dataLoading}
          paragraph={{ rows: 1 }}
          title={false}
        >
          <Row gutter={18}>
            <Col>
              <Tooltip title="Edit" placement="top" color="#3D4998">
                <EditOutlined
                  style={{
                    color: "#444653",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setOpenEmpModel({
                      open: true,
                      id: value,
                    });
                  }}
                />
              </Tooltip>
            </Col>
            <Col>
              <Tooltip title="Delete" placement="top" color="red">
                <DeleteOutlined
                  style={{
                    color: "red",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    setDeleteConfirmation({
                      open: true,
                      id: value,
                    })
                  }
                />
              </Tooltip>
            </Col>
          </Row>
        </Skeleton>
      ),
    },
  ];

  return (
    <Card
      size="small"
      className="TableCard"
      style={{
        marginTop: 35,
        boxShadow: "0px 0px 10px 0px #96A1C84D",
      }}
    >
      <div>
        <Row justify="space-between">
          <Col>
            <Row gutter={15}>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    setOpenEmpModel({
                      open: true,
                      id: "",
                    })
                  }
                >
                  Employee
                </Button>
              </Col>
            </Row>
          </Col>
          <Col>
            <Input
              allowClear
              style={{ width: "220px" }}
              placeholder="Search employee"
              onChange={(e: any) => {
                setDataLoading(true);
                if (!e.cancelable) {
                  const searchItemGroup = setTimeout(() => {
                    setSearchEmp(e.target.value);
                  }, 1000);
                  return () => clearTimeout(searchItemGroup);
                } else {
                  // console.log("Click cancle............");
                  setSearchEmp(e.target.value);
                }
              }}
              suffix={dataLoading || searchEmp ? "" : <SearchOutlined />}
            />
          </Col>
        </Row>
        <Table
          loading={dataLoading}
          className={`Tabel-style table-${
            themeData?.componentSize ?? "middle"
          }`}
          style={{ width: "100%", marginTop: "10px" }}
          columns={depttCols}
          dataSource={empList}
        />
      </div>
      {openEmpModel && (
        <AddEmpModel
          isModalOpen={openEmpModel.open}
          setIsModalOpen={setOpenEmpModel}
          id={openEmpModel.id}
          setTrigger={setAddUpdateTrigger}
        />
      )}
      {deleteConfirmation && (
        <ConfirmationModal
          open={deleteConfirmation.open}
          onNo={() =>
            setDeleteConfirmation({
              open: false,
              id: "",
            })
          }
          onYes={() => handleDelete(deleteConfirmation?.id)}
          text={`Are you sure you want to delete this row?`}
        />
      )}
    </Card>
  );
};

export default Employees;
